import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BadgeCheck, Flame, Droplets, Zap, MapPin, FileCheck, User, Users, Gauge, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';

const WhyChooseGreenFrog = () => {
  const [openFaqIndex, setOpenFaqIndex] = React.useState(null);

  const counties = [
    "Bedfordshire", "Berkshire", "Bristol", "Buckinghamshire", "Cambridgeshire", "Cheshire", "City of London", "Cornwall", "Cumbria", "Derbyshire", "Devon", "Dorset", "County Durham", "East Riding of Yorkshire", "East Sussex", "Essex", "Gloucestershire", "Greater London", "Greater Manchester", "Hampshire", "Herefordshire", "Hertfordshire", "Isle of Wight", "Kent", "Lancashire", "Leicestershire", "Lincolnshire", "Merseyside", "Norfolk", "North Yorkshire", "Northamptonshire", "Northumberland", "Nottinghamshire", "Oxfordshire", "Rutland", "Shropshire", "Somerset", "South Yorkshire", "Staffordshire", "Suffolk", "Surrey", "Tyne and Wear", "Warwickshire", "West Midlands", "West Sussex", "West Yorkshire", "Wiltshire", "Worcestershire",
    "Blaenau Gwent", "Bridgend", "Caerphilly", "Cardiff", "Carmarthenshire", "Ceredigion", "Conwy", "Denbighshire", "Flintshire", "Gwynedd", "Isle of Anglesey", "Merthyr Tydfil", "Monmouthshire", "Neath Port Talbot", "Newport", "Pembrokeshire", "Powys", "Rhondda Cynon Taf", "Swansea", "Torfaen", "Vale of Glamorgan", "Wrexham"
  ];

  const metaDescription = "Nine reasons developers and commercial sites choose Green Frog Utilities for gas, water and electrical connections: GIRS, WIRS and NERS accredited, national coverage, end to end delivery, dedicated project managers and meter asset management in one group.";

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Why Choose Green Frog Utilities",
    "url": "https://greenfrogutilities.co.uk/why-choose-green-frog",
    "about": {
      "@type": "Organization",
      "name": "Green Frog Utilities",
      "url": "https://greenfrogutilities.co.uk"
    },
    "description": metaDescription
  };

  const faqData = [
    {
      question: "Why use one contractor for gas, water and electric?",
      answer: "Because the three connections share the same trench, the same programme and the same site constraints. One accredited contractor designs them together and connects them on one timeline, which removes the delays that come from three companies waiting on each other."
    },
    {
      question: "Does Green Frog Utilities cover my area?",
      answer: "Green Frog Utilities delivers across England and Wales from its base in Tamworth, Staffordshire. Every service page lists the counties covered."
    },
    {
      question: "Who will manage my project?",
      answer: "A dedicated project manager is assigned at the start and stays with the project through to the live connection, dealing with the network operators on the site's behalf."
    },
    {
      question: "Can Green Frog Utilities also handle the meter?",
      answer: "Yes. Metering is delivered inside the group by Green Frog Asset Management, which holds MAM and AMI accreditation, so the meter is part of the same programme as the connection."
    },
    {
      question: "What kind of sites does Green Frog Utilities work on?",
      answer: "Commercial and industrial sites, new build developments, retail, mixed use and manufacturing, for developers, main contractors and site owners."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://greenfrogutilities.co.uk/" },
      { "@type": "ListItem", "position": 2, "name": "Why Choose Green Frog Utilities", "item": "https://greenfrogutilities.co.uk/why-choose-green-frog" }
    ]
  };

  const reasons = [
    {
      icon: Flame,
      title: "GIRS Accredited",
      desc: "Registered under the Gas Industry Registration Scheme, audited by LRQA, so gas mains and services we lay are adopted by the network.",
      link: "/services/connections",
      linkText: "Gas connections page"
    },
    {
      icon: Droplets,
      title: "WIRS Accredited",
      desc: "Registered under the Water Industry Registration Scheme as a self lay provider, so water mains we lay are adopted by the water company.",
      link: "/water-connections",
      linkText: "Water connections page"
    },
    {
      icon: Zap,
      title: "NERS Accredited",
      desc: "Registered under the National Electricity Registration Scheme as an independent connection provider, adopted by the distribution network.",
      link: "/electrical-connections",
      linkText: "Electrical connections page"
    },
    {
      icon: MapPin,
      title: "National Coverage",
      desc: "Delivering across England and Wales from our Midlands base in Tamworth, with the full county list on every service page.",
      link: null,
      linkText: null
    },
    {
      icon: FileCheck,
      title: "End-to-End Delivery",
      desc: "Design, network applications, excavation, mains, services and commissioning under one contract and one programme.",
      link: null,
      linkText: null
    },
    {
      icon: User,
      title: "Dedicated Project Managers",
      desc: "A named project manager from first drawing to live connection, one point of contact for the client, the network and the site.",
      link: null,
      linkText: null
    },
    {
      icon: Users,
      title: "Gas, Water & Electric Coordination",
      desc: "All three utilities designed together, trenched together where possible, and connected on one timeline instead of three.",
      link: null,
      linkText: null
    },
    {
      icon: Gauge,
      title: "Meter Asset Management Capability",
      desc: "Metering installed, owned and managed inside the group by Green Frog Asset Management, MAM and AMI accredited.",
      link: "/metering",
      linkText: "Metering"
    },
    {
      icon: Building2,
      title: "Commercial & Industrial Specialists",
      desc: "Developers, main contractors, factories, retail and mixed use sites, with the capacity and accreditation those projects require.",
      link: null,
      linkText: null
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div>
      <SEO 
        title="Why Choose Green Frog Utilities | Accredited Multi Utility Contractor, England and Wales"
        description={metaDescription}
        canonical="/why-choose-green-frog"
        jsonLd={[webPageSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <BadgeCheck className="h-16 w-16" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Why Choose Green Frog Utilities
            </h1>
            <p className="text-xl text-emerald-100 max-w-4xl mx-auto leading-relaxed">
              One accredited contractor for gas, water, electric and metering, from design to a live connection.
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-600 text-lg leading-relaxed">
              Most connection projects fail on coordination, not engineering. Three utilities, three contractors, three programmes, and a meter installer at the end who was never part of the plan. Green Frog Utilities is built to remove that. Here is what that means in practice.
            </p>
          </div>
        </div>
      </section>

      {/* Nine Reasons Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reasons.map((reason, index) => (
              <Card key={index} className="border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-emerald-100 p-3 rounded-lg flex-shrink-0">
                      <reason.icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{reason.title}</h3>
                      <p className="text-gray-600 text-sm mb-2">{reason.desc}</p>
                      {reason.link && (
                        <Link to={reason.link} className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">
                          {reason.linkText} &rarr;
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What a Project Looks Like */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            What a Green Frog Utilities project looks like
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">One contract</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">One project manager</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">One live date</div>
            </div>
          </div>
          <p className="text-gray-600 text-lg text-center max-w-3xl mx-auto">
            The connection, the coordination and the meter, all accountable to the same company.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openFaqIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where We Work Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Where we work</h2>
          <p className="text-gray-600 mb-6">
            Green Frog Utilities delivers gas, water and electrical connections across England and Wales, from a single accredited contractor.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
            {counties.map((county, index) => (
              <span key={index} className="text-sm text-gray-500">{county}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to start your project?
            </h2>
            <Link to="/contact">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseGreenFrog;
